import * as Date from 'dg-web-shared/lib/Date.ts';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Conditional } from 'dg-web-shared/lib/ReactHelpers.tsx';
import {
    GithubRelease,
    LATEST_GITHUB_RELEASE_OVERVIEW_URL,
} from 'dg-web-shared/model/GithubRelease.ts';
import { Translation } from '../../common/i18n/Text.ts';
import { TextButton } from '../../ui/buttons/TextButton.tsx';
import { LabeledText } from '../../ui/labeled-elements/LabeledText.tsx';
import { BulletList, Item } from '../../ui/layout/BulletList.tsx';
import { Paragraph } from '../../ui/layout/Text.tsx';
import {
    Notification,
    NotificationColor,
    NotificationIcon,
} from '../../ui/notifications/Notification.tsx';
import {
    HalfSlideIn,
    LoaderHeader,
    SlideInBody,
    SlideInHeaderTexts,
    StandardFirstLevelHeader,
} from '../../ui/slidein/Slidein.tsx';
import * as SyncAppActions from '../actions/SyncAppActions.ts';
import { syncAppTexts } from '../i18n/SyncAppTexts.ts';
import * as SyncAppHelpSlideInState from '../state/SyncAppHelpSlideInState.ts';

export interface SyncAppTexts {
    SyncApp: Translation;
    SyncAppRequired: Translation;
    SlideInHeaderCaption: Translation;
    NotInstalledHint: Translation;
    AppIntro: Translation;
    DownloadLatestVersion: Translation;
    Download: Translation;
    InstallationAndDownload: Translation;
    LatestVersion: Translation;
    UsageInfo: Translation;
    SystemRequirementsLabel: Translation;
    SystemRequirementsValue: Translation;
    Functionality: Translation;
    ImportCollectionData: Translation;
    ConfigureUsbSticks: Translation;
    UpdateTomConfigurations: Translation;
    IdentifyUsbStick: Translation;
    AutoUpdate: Translation;
}

function getInstallerDownloadUrl(release: GithubRelease): string {
    const executables = release.assets.filter(
        a => a.content_type === 'application/x-msdownload',
    );
    if (executables.length > 0) {
        return executables[0].browser_download_url;
    }
    return LATEST_GITHUB_RELEASE_OVERVIEW_URL;
}

export function SyncAppSlideIn() {
    const { storeState, update } = useStore(store => ({
        layout: SyncAppHelpSlideInState.Layout.get(store),
        latestRelease: SyncAppHelpSlideInState.LatestRelease.get(store),
    }));
    const { language } = useLanguage();

    const lng = language;
    const txt = syncAppTexts[lng];

    if (storeState.layout.open) {
        if (
            storeState.latestRelease.pending ||
            !storeState.latestRelease.data
        ) {
            return (
                <HalfSlideIn open={true}>
                    <LoaderHeader title={txt.SlideInHeaderCaption()} />
                </HalfSlideIn>
            );
        } else {
            const latestVersionInfo = storeState.latestRelease.data;

            const releaseDate = Date.Formatter.dayMonthYear(
                latestVersionInfo.published_at,
            );

            return (
                <HalfSlideIn open={true}>
                    <SlideInBody>
                        <Paragraph>{txt.AppIntro()}</Paragraph>
                        <Paragraph>{txt.UsageInfo()}</Paragraph>
                        <LabeledText label={txt.Functionality()}>
                            <BulletList>
                                <Item>{txt.ImportCollectionData()}</Item>
                                <Item>{txt.ConfigureUsbSticks()}</Item>
                                <Item>{txt.UpdateTomConfigurations()}</Item>
                                <Item>{txt.IdentifyUsbStick()}</Item>
                                <Item>{txt.AutoUpdate()}</Item>
                            </BulletList>
                        </LabeledText>
                        <Conditional c={storeState.layout.showNotInstalledHint}>
                            <Notification
                                title={txt.SyncAppRequired()}
                                color={NotificationColor.blue}
                                icon={NotificationIcon.info}
                            >
                                {txt.NotInstalledHint()}
                            </Notification>
                        </Conditional>
                        <LabeledText label={txt.LatestVersion()}>
                            {latestVersionInfo.name} ({releaseDate})
                        </LabeledText>
                        <LabeledText label={txt.SystemRequirementsLabel()}>
                            {txt.SystemRequirementsValue()}
                        </LabeledText>
                        <a href={getInstallerDownloadUrl(latestVersionInfo)}>
                            <TextButton
                                onClick={() => {}}
                                label={txt.DownloadLatestVersion()}
                            />
                        </a>
                    </SlideInBody>
                    <StandardFirstLevelHeader
                        onClose={() =>
                            update(SyncAppActions.closeSyncAppSlideIn)
                        }
                    >
                        <SlideInHeaderTexts
                            title={txt.SlideInHeaderCaption()}
                            hasLeftIcon={false}
                        />
                    </StandardFirstLevelHeader>
                </HalfSlideIn>
            );
        }
    } else {
        return <HalfSlideIn open={false} />;
    }
}
